import SwiperCore, { Autoplay, Pagination } from 'swiper';
import { useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from 'next/image';
import Link from 'next/link';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import { MediaQuery, useMediaQuery } from '../../../hooks/useMediaQuery';
import { getSlidesPerView } from '../../../utils/carousel';
import {
  oneAndOneFifthSlidesPerView,
  oneSlidePerView
} from '../../../constants/modals';
import { autoplayProps } from '../../../config/carousel';
import { BannerWithCarouselModel } from '../../../types/banner-with-carousel';
import * as S from './styles';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

/* eslint-disable-next-line */
type ImageCarouselProps = {
  carouselData: BannerWithCarouselModel[];
};

export function ImageCarousel(props: ImageCarouselProps) {
  const { carouselData } = props;

  const sliderRef = useRef() as any;

  const isDesktop = useMediaQuery(MediaQuery.LG);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  const navigation = (
    <div className="hidden xl:block">
      <BsChevronLeft
        className="navigation absolute -left-12 top-2/4 -translate-y-2/4 text-header3 text-secondaryBlack z-20 cursor-pointer"
        onClick={handlePrev}
      />
      <BsChevronRight
        className="navigation absolute -right-12 top-2/4 -translate-y-2/4 text-header3 text-secondaryBlack z-20 cursor-pointer"
        onClick={handleNext}
      />
    </div>
  );

  const imageHeight = isDesktop ? 800 : 600;
  const imageWidth = isDesktop ? 1400 : 1200;
  const slidesPerView = getSlidesPerView(
    oneAndOneFifthSlidesPerView,
    oneSlidePerView,
    isDesktop
  );

  const slides = carouselData.map((carouselItem) => {
    const { bgImage, ctaLink, description, title } = carouselItem;

    return (
      <SwiperSlide key={title}>
        <div className="relative">
          <Link href={ctaLink as string} passHref>
            <a>
              <Image
                alt="carousel image"
                blurDataURL="/icons/blur-placeholder.png"
                height={imageHeight}
                layout="responsive"
                objectFit="cover"
                placeholder="blur"
                width={imageWidth}
                src={bgImage}
              />
            </a>
          </Link>
          <div className="absolute bottom-0 py-2 sm:py-5 w-full flex flex-col justify-center items-start text-white text-left z-50 bg-black/50">
            <div className="px-5">
              <p className="text-subtitle sm:text-header5 font-MuseoSans100">
                {title}
              </p>
              <p className="text-caption sm:text-body sm:mb-0 font-MuseoSans100 font-extralight">
                {description}
              </p>
            </div>
          </div>
        </div>
      </SwiperSlide>
    );
  });

  return (
    <S.SwiperContainer>
      {navigation}
      <Swiper
        autoplay={autoplayProps}
        centeredSlides={true}
        className="imageCarousel"
        loop={true}
        modules={[Autoplay, Pagination]}
        onInit={(core: SwiperCore) => {
          sliderRef.current = core.el;
        }}
        pagination={{ clickable: true }}
        slidesPerView={slidesPerView}
        spaceBetween={20}
        speed={900}
        style={{ position: 'unset' }}
      >
        {slides}
      </Swiper>
    </S.SwiperContainer>
  );
}

export default ImageCarousel;
